/* eslint-disable max-lines */
import { skipToken } from '@reduxjs/toolkit/query';
import { BenefitTypes } from '@strata/constants';
import {
    DateFormats,
    formatDate,
    formatNameLastFirst,
    getProductName,
} from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import {
    FormTitleLabelValuePair,
    HeaderLabel,
    HeaderLabelValuePair,
} from '@strata/react-label-value-pairs';
import { Timeline } from '@strata/react-timeline';
import { SuccessToast, WarningToast } from '@strata/react-toast-message';
import { Form, Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ReactElement, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { MilestoneDialog } from './dialogs/MilestoneDialog';
import { UnsavedChangesDialog } from './dialogs/UnsavedChangesDialog';
import { IntakeCasePausedIcon } from './IntakeCasePausedIcon';
import { OverviewTab } from './OverviewTab';
import { ProductLabel } from './ProductLabel';

import { IntakeCaseDetailsActionsMenu } from 'components/intakeCases/IntakeCaseDetailsActionsMenu';
import { NotesTab } from 'components/intakeCases/NotesTab';
import { PriorAuth } from 'components/intakeCases/PriorAuth';
import { getIntakeStatusDetails } from 'components/shared/IntakeStatusDetails';
import { LighthouseIntakeStatusIndicator } from 'components/shared/LighthouseIntakeStatusIndicator';
import {
    type ActionMenuDialogType,
    DEFAULT_PHARMACY_PLACE_OF_SERVICE,
    MilestoneTitleDisplayStrings,
    MilestoneType,
    type PendingAction,
    PendingActionTypes,
    Permissions,
} from 'services/constants';
import { hasIntakeCaseBeenArchived } from 'services/helpers';
import { mapToIntakeCaseSummary } from 'services/mappers';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useGetLighthouseUserNameMapQuery, useGetUserProfileQuery } from 'store/api/authApi';
import { useGetEPaCasesQuery } from 'store/api/epaApi';
import {
    type IntakeCase,
    type IntakeCaseMilestone,
    type MappedMilestone,
    type TooltipContent,
    useGetIntakeCaseDetailsQuery,
    useGetMilestonesQuery,
    usePutUpdateIntakeCaseMutation,
    useUpdatePrescriberPreferenceMutation,
} from 'store/api/intakeCaseApi';
import { useGetProductsMapQuery, useGetSegmentsQuery } from 'store/api/referenceApi';
import { IntakeFormSectionSchemas } from 'store/intakeForm/intakeFormSchema';

const UnverifiedMemberInlineMsg = () => (
    <InlineMessage
        intent={InlineMessage.Intent.ERROR}
        label="The patient associated to this intake case is not verified."
    />
);

const IneligibleMemberInlineMsg = () => (
    <InlineMessage
        intent={InlineMessage.Intent.WARNING}
        label="The patient associated to this intake case failed benefit check eligibility."
    />
);

const UnverifiedPrescriberInlineMsg = () => (
    <InlineMessage
        intent={InlineMessage.Intent.WARNING}
        label="The prescriber associated to this intake case is not verified."
    />
);

const CaseLockedInlineMsg = () => (
    <InlineMessage
        intent={InlineMessage.Intent.INFO}
        label="This intake case is not assigned to you. Only the assigned user can edit this intake case."
    />
);

const MonitoringCaseInlineMsg = () => (
    <InlineMessage
        intent={InlineMessage.Intent.INFO}
        label="This is a monitoring intake case. Typically, no manual tracking or actions are needed."
    />
);

type IntakeCasesGridRoute = Extract<(typeof AppRoutes)[keyof typeof AppRoutes], string>;

const getIntakeCasesListBackLink = (
    isArchived: boolean,
    isMonitoring: boolean,
): { label: string; id: string; to: IntakeCasesGridRoute } => {
    if (isArchived) {
        return {
            label: 'Archived Intake Cases',
            id: 'archived-intake-cases-page-title-button',
            to: AppRoutes.archivedIntakeCases,
        };
    }

    if (isMonitoring) {
        return {
            label: 'Monitoring Intake Cases',
            id: 'monitoring-intake-cases-page-title-button',
            to: AppRoutes.monitoringIntakeCases,
        };
    }

    return {
        label: 'Active Intake Cases',
        id: 'active-intake-cases-page-title-button',
        to: AppRoutes.activeIntakeCases,
    };
};

const userAssignedToCase = (userUpn: string | undefined, assignedUpn: string | undefined) =>
    userUpn === assignedUpn;

const getTabHash = (tab: string) => tab.replace(/\s/g, '-').toLocaleLowerCase();

export const UserEventTooltip = ({ data = undefined }: TooltipContent) => (
    <>
        <div>
            <span className="font-semi-bold--capitalize">User: </span>
            {data?.username}
        </div>
        <div>
            <span className="font-semi-bold--capitalize">Date: </span>
            {formatDate(data?.date, DateFormats.DATE_TIME)}
        </div>
        {data?.communication_direction && (
            <div>
                <span className="font-semi-bold--capitalize">Direction: </span>
                {data.communication_direction}
            </div>
        )}
        {data?.outreach_organization && (
            <div>
                <span className="font-semi-bold--capitalize">Organization: </span>
                {data.outreach_organization}
            </div>
        )}
        {data?.communication_type && (
            <div>
                <span className="font-semi-bold--capitalize">Type: </span>
                {data.communication_type}
            </div>
        )}
        {data?.submit_method && (
            <div>
                <span className="font-semi-bold--capitalize">Submit Method: </span>
                {data.submit_method}
            </div>
        )}
        {data?.submit_id && (
            <div>
                <span className="font-semi-bold--capitalize">Submit ID: </span>
                {data.submit_id}
            </div>
        )}
        {data?.action_plan && (
            <div>
                <span className="font-semi-bold--capitalize">Action Plan: </span>
                {data.action_plan}
            </div>
        )}
        {data?.note && (
            <div>
                <span className="font-semi-bold--capitalize">Note: </span>
                {data.note}
            </div>
        )}
        {data?.canceled_reason && (
            <div>
                <span className="font-semi-bold--capitalize">Reason: </span>
                {data.canceled_reason}
            </div>
        )}
    </>
);

export const IntakeCaseDetails = () => {
    const { intakeCaseId } = useParams() as { intakeCaseId: string };
    const navigate = useNavigate();
    const location = useLocation();

    const permissions = useAppSelector((state) => state.auth.permissions);

    const { data: intakeCaseDetails, isFetching: intakeCaseDetailsFetching } =
        useGetIntakeCaseDetailsQuery({
            intakeCaseId,
        });

    const isArchivedIntakeCase = hasIntakeCaseBeenArchived(intakeCaseDetails?.intakeStatus);
    const isMonitoringIntakeCase = intakeCaseDetails?.monitoringCase === true;
    const intakeCasesListBackLink = getIntakeCasesListBackLink(
        isArchivedIntakeCase,
        isMonitoringIntakeCase,
    );
    const { data: profile } = useGetUserProfileQuery();

    const locked = !userAssignedToCase(profile?.email, intakeCaseDetails?.assignedUsername);
    const inputsDisabled =
        isArchivedIntakeCase || !permissions.includes(Permissions.WRITE_INTAKE_CASE) || locked;

    const { data: segments } = useGetSegmentsQuery();
    const { data: productsMap } = useGetProductsMapQuery();

    const intakeCaseDetailsSchema = useMemo(() => {
        let flatSchema = Yup.object();
        Object.values(IntakeFormSectionSchemas).forEach((schema) => {
            flatSchema = flatSchema.concat(schema);
        });
        return flatSchema;
    }, []);

    const [putUpdateIntakeCaseMutation] = usePutUpdateIntakeCaseMutation();
    const [updatePrescriberPreference] = useUpdatePrescriberPreferenceMutation();

    const { data: apiMilestones = [] } = useGetMilestonesQuery({
        intakeCaseId,
    });
    const { data: userNameMap } = useGetLighthouseUserNameMapQuery();

    const getUserName = (email?: string) => (email ? userNameMap?.[email] : 'Unassigned');
    const { data: epaCases, isFetching: epaCasesFetching } = useGetEPaCasesQuery(
        intakeCaseId ? { intakeCaseId } : skipToken,
    );

    const [selectedMilestone, setSelectedMilestone] = useState<IntakeCaseMilestone | undefined>(
        undefined,
    );
    const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingAction>(null);
    const unsavedChangesSourceRef = useRef<'overview' | 'prior-auth'>('overview');
    const [priorAuthUnsavedChangesState, setPriorAuthUnsavedChangesState] = useState<{
        hasUserChanges: boolean;
        isSubmitting: boolean;
        onSave: () => Promise<boolean>;
        onDiscard: () => void;
    }>({
        hasUserChanges: false,
        isSubmitting: false,
        onSave: () => Promise.resolve(false),
        onDiscard: () => {},
    });
    const [openActionMenuDialog, setOpenActionMenuDialog] = useState<ActionMenuDialogType | null>(
        null,
    );

    const intakeCaseMilestonesData = useMemo(
        () =>
            apiMilestones.map((milestone) => {
                const mappedMilestone: MappedMilestone = {
                    title: milestone.title,
                    status: milestone.status,
                    timestamp: milestone.timestamp,
                    eventType: milestone.event_type,
                    additionalContent: milestone.message ? <div>{milestone.message}</div> : null,
                    userType: milestone.username ? Timeline.UserTypes.FMH : undefined,
                    tooltipContent:
                        milestone.event_type === Timeline.EventTypes.USER ? (
                            <UserEventTooltip data={milestone.tooltip_data} />
                        ) : null,
                    additionalData: milestone.additional_data,
                };
                if (
                    milestone.title in MilestoneTitleDisplayStrings &&
                    milestone.additional_data?.epa_transaction_id
                ) {
                    const actionButtonLabel =
                        milestone.title === MilestoneType.QUESTIONS_SUBMITTED
                            ? 'Review Question Set'
                            : 'View Details';
                    mappedMilestone.action = {
                        label: actionButtonLabel,
                        onClick: () => setSelectedMilestone(milestone),
                    };
                }
                return mappedMilestone;
            }),
        [apiMilestones],
    );

    const { showIntakeCasePauseOption, showLighthousePriorAuthUi } = useFlags<{
        showIntakeCasePauseOption: boolean;
        showLighthousePriorAuthUi: boolean;
    }>();
    const pausedModel = intakeCaseDetails && mapToIntakeCaseSummary(intakeCaseDetails);

    return (
        <Formik
            initialValues={
                {
                    ...intakeCaseDetails,
                    benefit: intakeCaseDetails?.benefit ?? BenefitTypes.PHARMACY,
                    placeOfService:
                        intakeCaseDetails?.placeOfService ?? DEFAULT_PHARMACY_PLACE_OF_SERVICE,
                    isBrand: intakeCaseDetails?.isBrand ?? false,
                } as IntakeCase
            }
            enableReinitialize
            validateOnMount
            validationSchema={intakeCaseDetailsSchema}
            onSubmit={async (values, helpers) => {
                await putUpdateIntakeCaseMutation(values).unwrap();
                if (values.prescriberNpi && values.prescriberPref) {
                    await updatePrescriberPreference({
                        prescriberPreference: values.prescriberPref,
                        prescriberNpi: values.prescriberNpi,
                    }).unwrap();
                }
                helpers.resetForm({ values });
                SuccessToast({ message: 'Intake Case saved successfully.' });
            }}
        >
            {(formik) => {
                const activeTab = (
                    location.hash.replace(/^#/, '').replace(/-/g, ' ') || 'overview'
                ).toLowerCase();
                const isPriorAuthTabActive = activeTab === 'prior auths';
                const openUnsavedChangesDialog = (
                    action: NonNullable<PendingAction>,
                    source: 'overview' | 'prior-auth',
                ) => {
                    unsavedChangesSourceRef.current = source;
                    setPendingAction(action);
                    setIsUnsavedChangesDialogOpen(true);
                };

                const closeUnsavedChangesDialog = () => {
                    setPendingAction(null);
                    setIsUnsavedChangesDialogOpen(false);
                };

                const runPendingAction = () => {
                    const action = pendingAction;
                    if (!action) {
                        return;
                    }
                    closeUnsavedChangesDialog();

                    switch (action.type) {
                        case PendingActionTypes.OPEN_OVERVIEW_TAB:
                            navigate({
                                ...location,
                                hash: getTabHash('Overview'),
                            });
                            break;
                        case PendingActionTypes.OPEN_PRIOR_AUTH_TAB:
                            navigate({
                                ...location,
                                hash: getTabHash('Prior Auths'),
                            });
                            break;
                        case PendingActionTypes.OPEN_ACTION_MENU_DIALOG:
                            setOpenActionMenuDialog(action.dialog);
                            break;
                        case PendingActionTypes.OPEN_NOTES_TAB:
                            navigate({
                                ...location,
                                hash: getTabHash('Notes'),
                            });
                            break;
                        default:
                            break;
                    }
                };

                const hasOverviewChanges = formik.dirty && Object.keys(formik.touched).length > 0;
                const hasPriorAuthChanges = priorAuthUnsavedChangesState.hasUserChanges;
                const hasUserChanges = isPriorAuthTabActive
                    ? hasPriorAuthChanges
                    : hasOverviewChanges;
                const unsavedChangesDialogIsSubmitting = isPriorAuthTabActive
                    ? priorAuthUnsavedChangesState.isSubmitting
                    : formik.isSubmitting;
                const shouldUsePriorAuthUnsavedHandlers =
                    unsavedChangesSourceRef.current === 'prior-auth' ||
                    (isPriorAuthTabActive && hasPriorAuthChanges);

                const handleActionWithUnsavedChangesGuard = (
                    action: NonNullable<PendingAction>,
                    immediateAction: () => void,
                ) => {
                    if (hasUserChanges) {
                        openUnsavedChangesDialog(
                            action,
                            isPriorAuthTabActive ? 'prior-auth' : 'overview',
                        );
                        return;
                    }

                    immediateAction();
                };

                const handleUnsavedChangesSave = async () => {
                    if (shouldUsePriorAuthUnsavedHandlers) {
                        const didSave = await priorAuthUnsavedChangesState.onSave();
                        if (!didSave) {
                            return;
                        }
                        runPendingAction();
                        return;
                    }

                    const errors = await formik.validateForm();

                    if (Object.keys(errors).length > 0) {
                        await formik.setTouched(
                            Object.fromEntries(Object.keys(errors).map((key) => [key, true])),
                        );
                        WarningToast({
                            message:
                                'Please fix the validation errors in the Overview tab before saving.',
                        });
                        return;
                    }

                    await formik.submitForm();
                    runPendingAction();
                };

                return (
                    <FormHeaderAndTabbedContent
                        rowOneContentHeader={
                            <>
                                {selectedMilestone && (
                                    <MilestoneDialog
                                        milestone={selectedMilestone}
                                        onSecondaryButtonClick={() =>
                                            setSelectedMilestone(undefined)
                                        }
                                        epaCases={epaCases}
                                        intakeCaseDetails={intakeCaseDetails}
                                    />
                                )}
                                <UnsavedChangesDialog
                                    isOpen={isUnsavedChangesDialogOpen}
                                    onCancel={closeUnsavedChangesDialog}
                                    onDiscard={() => {
                                        if (shouldUsePriorAuthUnsavedHandlers) {
                                            priorAuthUnsavedChangesState.onDiscard();
                                        } else {
                                            formik.resetForm();
                                        }
                                        runPendingAction();
                                    }}
                                    onSave={handleUnsavedChangesSave}
                                    isSubmitting={unsavedChangesDialogIsSubmitting}
                                />
                                <ActionButtonPageTitle
                                    label={intakeCasesListBackLink.label}
                                    id={intakeCasesListBackLink.id}
                                    title="Intake Case Details"
                                    onClick={() => navigate(intakeCasesListBackLink.to)}
                                />
                                <div className="flex__gap--medium" style={{ marginLeft: 'auto' }}>
                                    {intakeCaseDetails && (
                                        <IntakeCaseDetailsActionsMenu
                                            intakeCase={{ ...intakeCaseDetails, ...formik.values }}
                                            locked={locked}
                                            openDialog={openActionMenuDialog}
                                            onOpenDialog={setOpenActionMenuDialog}
                                            onCloseDialog={() => setOpenActionMenuDialog(null)}
                                            onActionWithUnsavedChangesGuard={
                                                handleActionWithUnsavedChangesGuard
                                            }
                                            useGuardForLogPriorAuth={showLighthousePriorAuthUi}
                                            isValidIntakeCase={formik.isValid}
                                        />
                                    )}
                                </div>
                            </>
                        }
                        rowTwoContentHeader={
                            !intakeCaseDetailsFetching && intakeCaseDetails ? (
                                <>
                                    <span className="flex__gap--large">
                                        {showIntakeCasePauseOption && pausedModel !== undefined && (
                                            <IntakeCasePausedIcon large intakeCase={pausedModel} />
                                        )}
                                        <FormTitleLabelValuePair
                                            label={formatNameLastFirst(
                                                intakeCaseDetails.memberLastName,
                                                intakeCaseDetails.memberFirstName,
                                            )}
                                            value={`DOB: ${formatDate(intakeCaseDetails.memberDateOfBirth)}`}
                                        />
                                    </span>
                                    <HeaderLabelValuePair
                                        label="Product"
                                        value={
                                            <ProductLabel
                                                product={getProductName(
                                                    productsMap,
                                                    intakeCaseDetails.productServiceOid,
                                                    intakeCaseDetails.isBrand,
                                                )}
                                                isNonFormulary={intakeCaseDetails.nonFormulary}
                                            />
                                        }
                                    />
                                    <HeaderLabelValuePair
                                        label="Health Plan"
                                        value={
                                            segments?.find(
                                                ({ segmentId }) =>
                                                    segmentId === intakeCaseDetails.segmentId,
                                            )?.name
                                        }
                                    />
                                    <HeaderLabelValuePair
                                        label="Prescriber"
                                        value={formatNameLastFirst(
                                            intakeCaseDetails.prescriberLastName,
                                            intakeCaseDetails.prescriberFirstName,
                                        )}
                                    />
                                    <HeaderLabelValuePair
                                        label="Created Date"
                                        value={formatDate(
                                            intakeCaseDetails.createDate,
                                            DateFormats.DATE_TIME,
                                        )}
                                    />
                                    {isArchivedIntakeCase && (
                                        <HeaderLabelValuePair
                                            label="Archived Date"
                                            value={formatDate(
                                                intakeCaseDetails.archivedDate,
                                                DateFormats.DATE_TIME,
                                            )}
                                        />
                                    )}
                                    <div>
                                        <HeaderLabelValuePair
                                            label="Assignee"
                                            value={getUserName(intakeCaseDetails.assignedUsername)}
                                        />
                                    </div>
                                    <div>
                                        <HeaderLabel label="Intake Status" />
                                        <LighthouseIntakeStatusIndicator
                                            status={intakeCaseDetails.intakeStatus}
                                        />
                                    </div>
                                    <div>
                                        <HeaderLabelValuePair
                                            label="Status Details"
                                            value={getIntakeStatusDetails(
                                                intakeCaseDetails.intakeStatusDetails,
                                            )}
                                        />
                                    </div>
                                </>
                            ) : undefined
                        }
                        rowThreeContentHeader={(() => {
                            const issues: ReactElement[] = [];
                            if (formik.values.memberVerified === false) {
                                issues.push(<UnverifiedMemberInlineMsg key="unverifiedMember" />);
                            } else if (formik.values.memberEligible === false) {
                                issues.push(<IneligibleMemberInlineMsg key="ineligibleMember" />);
                            }
                            if (formik.values.prescriberVerified === false) {
                                issues.push(
                                    <UnverifiedPrescriberInlineMsg key="unverifiedPrescriber" />,
                                );
                            }
                            if (locked) {
                                issues.push(<CaseLockedInlineMsg key="caseLocked" />);
                            }
                            if (isMonitoringIntakeCase) {
                                issues.push(<MonitoringCaseInlineMsg key="monitoringCase" />);
                            }
                            return issues.length > 0 ? (
                                <div
                                    className="flex__gap--small"
                                    style={{ flexDirection: 'column', width: '100%' }}
                                >
                                    {issues}
                                </div>
                            ) : null;
                        })()}
                        tabs={[
                            {
                                title: 'Overview',
                                mainContent: (
                                    <Form style={{ height: '100%' }}>
                                        <OverviewTab
                                            intakeCaseDetails={intakeCaseDetails}
                                            intakeCaseDetailsFetching={intakeCaseDetailsFetching}
                                            inputsDisabled={inputsDisabled}
                                            intakeCaseMilestonesData={intakeCaseMilestonesData}
                                            intakeCaseId={intakeCaseId}
                                        />
                                    </Form>
                                ),
                            },
                            ...(showLighthousePriorAuthUi
                                ? [
                                      {
                                          title: 'Prior Auths',
                                          mainContent: (
                                              <PriorAuth
                                                  intakeCaseDetails={intakeCaseDetails}
                                                  intakeCaseDetailsFetching={
                                                      intakeCaseDetailsFetching
                                                  }
                                                  intakeCaseMilestonesData={
                                                      intakeCaseMilestonesData
                                                  }
                                                  intakeCaseId={intakeCaseId}
                                                  epaCases={epaCases}
                                                  epaCasesFetching={epaCasesFetching}
                                                  locked={locked}
                                                  onUnsavedChangesStateChange={
                                                      setPriorAuthUnsavedChangesState
                                                  }
                                              />
                                          ),
                                      },
                                  ]
                                : []),
                            {
                                title: 'Notes',
                                className: 'tabbed-grid-content',
                                mainContent: <NotesTab />,
                            },
                        ]}
                        activeTab={location.hash.replace(/^#/, '').replace(/-/g, ' ')}
                        onTabSelect={(tab) => {
                            const nextHash = getTabHash(tab);
                            const isLeavingPriorAuthTab =
                                isPriorAuthTabActive && tab !== 'Prior Auths';

                            if (isLeavingPriorAuthTab && hasPriorAuthChanges) {
                                switch (tab) {
                                    case 'Overview':
                                        openUnsavedChangesDialog(
                                            {
                                                type: PendingActionTypes.OPEN_OVERVIEW_TAB,
                                            },
                                            'prior-auth',
                                        );
                                        return false;

                                    case 'Notes':
                                        openUnsavedChangesDialog(
                                            {
                                                type: PendingActionTypes.OPEN_NOTES_TAB,
                                            },
                                            'prior-auth',
                                        );
                                        return false;

                                    default:
                                        break;
                                }
                            }

                            if (!isPriorAuthTabActive && hasOverviewChanges) {
                                switch (tab) {
                                    case 'Prior Auths':
                                        openUnsavedChangesDialog(
                                            {
                                                type: PendingActionTypes.OPEN_PRIOR_AUTH_TAB,
                                            },
                                            'overview',
                                        );
                                        return false;

                                    case 'Notes':
                                        openUnsavedChangesDialog(
                                            {
                                                type: PendingActionTypes.OPEN_NOTES_TAB,
                                            },
                                            'overview',
                                        );
                                        return false;

                                    default:
                                        break;
                                }
                            }
                            navigate({
                                ...location,
                                hash: nextHash,
                            });
                            return true;
                        }}
                    />
                );
            }}
        </Formik>
    );
};
